"use client";
import React, { ReactNode } from "react";
import { Store } from "lucide-react";
import { cn } from "utils/utils";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";

interface StoreListProps {
  title: string;
  count?: string | number;
  stores: Array<{ id: number; name: string; isTradingHub?: boolean }>;
  emptyMessage: string;
  onStoreClick: (storeId: number) => void;
  helperText?: string;
  /** Rendered inline after the title, e.g. a save-state indicator */
  titleAdornment?: ReactNode;
  renderIcon: (index: number) => ReactNode;
  isLoading?: boolean;
  statusMessage?: string;
  maxHeight?: number;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  compact?: boolean;
}

export function StoreList({
  title,
  count,
  stores,
  emptyMessage,
  onStoreClick,
  helperText,
  titleAdornment,
  renderIcon,
  isLoading = false,
  statusMessage,
  maxHeight = 350,
  iconPosition = "left",
  disabled = false,
  compact = false,
}: StoreListProps) {
  const hasStores = stores.length > 0;

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col self-start",
        compact ? "gap-4" : "gap-3"
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between px-2">
          <h3 className="text-base-content flex min-w-0 items-center gap-2 font-medium">
            {title}
            {isLoading && <span className="loading loading-xs loading-dots" />}
            {titleAdornment}
          </h3>

          {count !== undefined && (
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  "badge badge-secondary",
                  !compact && "badge-soft badge-sm"
                )}
              >
                {count}
              </span>
              <Store
                size={ICON_SIZE}
                className="text-secondary"
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {compact && statusMessage && (
          <p className="text-base-content/60 px-2 text-sm">{statusMessage}</p>
        )}
      </div>

      <div
        className={cn(
          "rounded-box w-full overflow-y-auto px-1",
          compact && "max-h-[150px] lg:max-h-[350px]",
          !hasStores && "text-base-content/70 p-4 text-center"
        )}
        style={compact ? undefined : { maxHeight: `${maxHeight}px` }}
      >
        {hasStores ? (
          <>
            {!compact && statusMessage && (
              <p className="bg-base-200/80 text-base-content/65 rounded-field mx-1 mb-2 px-3 py-2 text-xs">
                {statusMessage}
              </p>
            )}
            <ul
              className={cn(
                "w-full p-1",
                compact ? "menu menu-sm gap-0.5 space-y-1" : "list gap-1"
              )}
            >
              {stores.map((store, index) => (
                <li
                  key={store.id}
                  className={cn(
                    "min-w-0",
                    compact ? "w-full" : "list-row grid-cols-1 p-0"
                  )}
                >
                  <button
                    className={cn(
                      "flex w-full items-center gap-2 text-left transition-colors duration-150",
                      compact
                        ? "btn btn-soft p-3"
                        : "border-base-content/10 bg-base-200/55 hover:bg-base-200 focus-visible:outline-primary rounded-field min-h-12 border px-3 py-2 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-45",
                      iconPosition === "right" && "justify-between"
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                      onStoreClick(store.id);
                    }}
                    disabled={disabled}
                    aria-label={`${iconPosition === "left" ? "Add" : "Remove"} ${store.name}`}
                  >
                    {iconPosition === "left" && renderIcon(index)}

                    <div
                      className={cn(
                        "flex min-w-0 flex-1 items-center gap-2",
                        iconPosition === "right" && "pr-2"
                      )}
                    >
                      <span
                        className="min-w-0 flex-1 truncate text-sm font-medium"
                        title={store.name}
                      >
                        {store.name}
                      </span>
                      {store.isTradingHub && (
                        <span className="badge badge-soft badge-secondary badge-xs shrink-0">
                          Trading hub
                        </span>
                      )}
                    </div>

                    {iconPosition === "right" && renderIcon(index)}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div>{compact ? emptyMessage : statusMessage || emptyMessage}</div>
        )}
      </div>

      {helperText && (
        <p className="text-base-content/60 text-xs italic">{helperText}</p>
      )}
    </div>
  );
}
